import { inject } from '@angular/core';
import {
  type HttpInterceptorFn,
  type HttpRequest,
  HttpContextToken,
  HttpErrorResponse,
} from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { SsoAuthService } from '../services/sso-auth.service';

export const SKIP_TOKEN = new HttpContextToken<boolean>(() => false);

export const tokenInterceptor: HttpInterceptorFn = (request, next) => {
  const oauthService = inject(OAuthService);
  const ssoAuthService = inject(SsoAuthService);

  if (request.context.has(SKIP_TOKEN) || isTokenRequest(request, oauthService)) {
    return next(request);
  }

  const token = ssoAuthService.getAccessToken();
  const authReq = addToken(request, token);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status !== 401 || isTokenRequest(request, oauthService)) {
        return throwError(() => error);
      }

      return from(refreshAccessToken(oauthService)).pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            ssoAuthService.logOut();
            return throwError(() => error);
          }

          const refreshedToken = ssoAuthService.getAccessToken();
          return next(addToken(request, refreshedToken));
        }),
      );
    }),
  );
};

function addToken(request: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
  if (!accessToken) {
    return request;
  }

  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${accessToken}`),
  });
}

let refreshInFlight: Promise<boolean> | null = null;

function refreshAccessToken(oauthService: OAuthService): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = oauthService
    .refreshToken()
    .then(() => true)
    .catch((error: unknown) => {
      console.error(error);
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function isTokenRequest(request: HttpRequest<unknown>, oauthService: OAuthService): boolean {
  const tokenEndpoint = oauthService.tokenEndpoint;
  if (!tokenEndpoint) {
    return false;
  }

  return request.url.startsWith(tokenEndpoint);
}
